import type { PhoneState } from '@app/components/Phone/types';
import type {
  CONFIRMED,
  NOVISIT,
  VISITED,
  WAITING,
} from '@app/constants/clientStatuses';
import type {
  LAST_MONTH,
  LAST_N_DAYS,
  LAST_THREE_MONTHS,
  LAST_WEEK,
  NEXT_MONTH,
  NEXT_N_DAYS,
  NEXT_THREE_MONTHS,
  NEXT_WEEK,
  PERIOD,
  TODAY,
  TOMORROW,
  YESTERDAY,
} from '@app/pages/Clients/ClientsList/constants/periods';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import type { NIntegrations, NRecords } from '@app/types';
import type { NClientServices } from '@app/types/ClientServices';
import type { NEmployees } from '@app/types/Employees';
import type { NSmallGlobalEntities } from '@app/types/SmallGlobalEntities';
import type { NTags } from '@app/types/Tags';

import type { FormInstance } from 'antd';
import type moment from 'moment';

export namespace NClients {
  export interface ClientAbstract {
    Name: string | null;
    Phone1: NSmallGlobalEntities.Phone;
  }

  export interface ClientCard {
    id?: number | null;
    isOpenFromCalendarModal?: boolean;
    isLeadCardComponent?: boolean;
  }

  export interface ClientLite extends ClientAbstract {
    Id: number;
    BranchId: number;
    Branch: string | null;
    Phone2: NSmallGlobalEntities.Phone;
    Email: string | null;
    Gender: NSmallGlobalEntities.Gender;
    IsDeleted: boolean;
    Age: number | null;
    MoneySpent: number;
    CountVisits: number;
    DateVisitFirst: string | null;
    DateVisitLast: string | null;
    Balance: number;
    VisitFrequency: string | null;
    ClientTags: Array<NTags.Tag | undefined> | null;
  }

  export interface AddClientFormValues {
    NameFirst: string;
    FirstPhone: string;
    FirstPhoneCountryId: number;
    Id?: number;
    DateBirth?: moment.Moment | null;
    Email?: string | null;
    Gender?: NSmallGlobalEntities.Gender;
    IsPhoneVerified?: boolean;
    Language?: string | null;
    NameLast?: string | null;
    NameMiddle?: string | null;
    SecondPhone?: string | null;
    SecondPhoneCountryId?: number | null;
    Tags?: Array<NTags.Tag | undefined> | null;
    LanguageId?: number | null;
    Comment: string | null;
    Phone1: PhoneState;
    Phone2: PhoneState;
    Snils: string | null;
    IdentityDocumentType: string | null;
    IdentityDocumentNumber: string | null;
    RegistrationAddress: string | null;
  }
  export interface ClientFull {
    Id?: ClientLite['Id'];
    NameLast: ClientAbstract['Name'];
    NameFirst: ClientAbstract['Name'];
    NameMiddle: ClientAbstract['Name'];
    Gender: NClients.ModelClient['Gender'];
    Email: ClientLite['Email'];
    Phone1: ClientAbstract['Phone1'];
    Phone1CountryId: NSmallGlobalEntities.PhoneCountryId;
    Phone1Code?: string | null;
    Phone2: ClientAbstract['Phone1'];
    Phone2CountryId: NSmallGlobalEntities.PhoneCountryId;
    Phone2Code?: string | null;
    DateBirth: string | null;
    IsDeleted?: ClientLite['IsDeleted'];
    ClientTags: Array<NTags.Tag | undefined> | null;
    Photo: string | null;
    IsPhoneVerified: boolean;
    BranchId?: ClientLite['BranchId'];
    Branch?: ClientLite['Branch'];
    Age?: ClientLite['Age'];
    LanguageId: number | null;
    Comment?: string | null;
    CompanyName?: string | null;
    Snils: string | null;
    IdentityDocumentType: string | null;
    IdentityDocumentNumber: string | null;
    RegistrationAddress: string | null;
  }

  export type CombinedClient = ClientFull & ClientLite;

  export interface ClientForView {
    Id: number;
    LeadId: number | null;
    NameLast: string | null;
    NameFirst: string | null;
    NameMiddle: string | null;
    DateBirth: string | null;
    Age: number | null;
    Phone1: string | null;
    Phone2: string | null;
    Email: string | null;
    Gender: string | null;
    Photo: string | null;
    IsPhoneVerified: boolean;
    IsDeleted?: boolean;
    ClientTags: NTags.Tag[] | null;
    CreateTime: string;
    HistoryFrom: string | null;
    HistoryActionIds: HistoryAction[] | null;
    Balance: number;
    CountVisits: number;
    MoneySpent: number;
    DateVisitFirst: string | null;
    DateVisitLast: string | null;
    LastRecordId: NRecords.ClientRecord['Id'] | null;
    LastSocialIntegration?: number | null;
    LastSocialIntegrationId?: number | null;
    Channels: ModelItem[] | null;
    IsClientUserNotSameBranch: boolean;
    ClientCrmTasks: NCrmTasks.ModelCrmTask[] | null;
    Comment: string | null;
    SocialIntegrations: NIntegrations.ModelLeadSocialIntegration[] | null;
    CompanyName: string | null;
  }

  export type ModelClientView = ClientForView;

  export interface HistoryAction {
    Key: string | null;
    Value: string | null;
  }

  export interface ModelItem {
    Id: number;
    IdS: string | null;
    Name: string | null;
    AccountName?: string | null;
  }

  export type Status =
    | typeof WAITING
    | typeof CONFIRMED
    | typeof VISITED
    | typeof NOVISIT;

  export type ModelClientsFilterData = {
    Employees: Array<{
      Id: NEmployees.Employee['Id'];
      Photo: string;
      NameFirst: NEmployees.Employee['NameFirst'];
      NameLast: NEmployees.Employee['NameLast'];
      IsDeleted: NEmployees.Employee['IsDeleted'];
    }>;
    Services: Array<{
      Id: NClientServices.Service['Id'];
      Name: NClientServices.Service['Name'];
      CategoryId: NClientServices.Category['Id'];
      CategoryName: NClientServices.Category['Name'];
      IsDeleted: NClientServices.Category['IsDeleted'];
    }>;
    RecordTags: Array<{
      Id: NTags.ClientTag['Id'];
      Name: NTags.ClientTag['Name'];
      IconId: NTags.ClientTag['IconId'];
      Color: NTags.ClientTag['Color'];
      IsDeleted: NTags.ClientTag['IsDeleted'];
    }>;
    ClientTags: Array<{
      Id: NTags.ClientTag['Id'];
      Name: NTags.ClientTag['Name'];
      IconId: NTags.ClientTag['IconId'];
      Color: NTags.ClientTag['Color'];
      IsDeleted: NTags.ClientTag['IsDeleted'];
    }>;
    CategoryServices: Array<{
      Id: NClientServices.Category['Id'];
      Name: NClientServices.Category['Name'];
      IsDeleted: NClientServices.Category['IsDeleted'];
    }>;
  };

  export type HandleTopClientBalance = {
    Amount: string;
    Comment: string | null;
    PaymentAccountId: number;
    form: FormInstance;
  };

  export type ModelClientLite = {
    Id: number;
    Name: string | null;
    NameFirst: string | null;
    NameLast: string | null;
    NameMiddle: string | null;
    BranchId: number;
    Branch: string | null;
    Phone1: string | null;
    Phone1CountryId: number | null;
    IsPhoneVerified: boolean;
    Phone2: string | null;
    Phone2CountryId: number | null;
    Email: string | null;
    DateBirth: string | null;
    Age: number | null;
    Gender: NSmallGlobalEntities.Gender;
    IsDeleted: boolean;
    Balance: number;
    MoneySpent: number;
    DateVisitFirst: string | null;
    DateVisitLast: string | null;
    CountVisits: number;
    VisitFrequency: string | null;
    LeadId?: number | null;
    HasOpenTasks?: boolean | null;
    IsLoyaltyCard?: boolean | null;
  };

  export type ModelClientLead = {
    IntegrationId: number;
    Phone: string | null;
    ClientName: string | null;
  };

  export type GeneralFilterItem = {
    key: string;
    label: JSX.Element;
    children?: GeneralFilterItem[];
  };

  export type ModelClient = {
    Id: number;
    BranchId: number;
    NameLast: string | null;
    NameFirst: string | null;
    NameMiddle: string | null;
    Gender: NSmallGlobalEntities.Gender;
    Email: string | null;
    Phone1: string | null;
    Phone1CountryId: number | null;
    Phone2: string | null;
    Phone2CountryId: number | null;
    DateBirth: string | null;
    IsPhoneVerified: boolean;
    IsDeleted: boolean;
    Photo: string | null;
    LanguageId: number | null;
    Language: string | null;
    ClientTags: NTags.ModelTag[] | null;
    MigrateId: number | null;
    Comment: string | null;
    CompanyName?: string | null;
    IsHuman: boolean | null;
    LeadId?: number | null;
    Snils: string | null;
    IdentityDocumentType: string | null;
    IdentityDocumentNumber: string | null;
    RegistrationAddress: string | null;
  };

  export type ModelClientSearchResult = {
    Id: number;
    Filter: ModelClientSearchFilter;
    Clients: ModelClientLite[] | null;
    PageN: number;
    CountPages: number;
    CountClients: number;
  };

  export type ModelClientSearchFilter = {
    SearchTerm: string | null;
    ClientsPerPage: number;
    Filters: ModelFilter[] | null;
  };

  export type ModelFilter = {
    VisitsFilter: ModelVisitFilterGroupFilter[] | null;
    ClientsFilter: ModelClientFilterGroupFilter[] | null;
    SalesFilter: ModelSaleFilterGroupFilter[] | null;
  };

  export type ModelVisitFilter = {
    HasRecords: boolean | null;
    Statuses: string[] | null;
    MinCountRecords: number | null;
    MaxCountRecords: number | null;
    DateStart: string | null;
    DateEnd: string | null;
    Period: Period;
    OffsetDays: number | null;
    EmployeeIds: number[] | null;
    ServiceCategoryIds: number[] | null;
    ServiceIds: number[] | null;
    MinServicesCost: number | null;
    MaxServicesCost: number | null;
    TagIds: number[] | null;
  };

  export type Period =
    | typeof TODAY
    | typeof YESTERDAY
    | typeof LAST_WEEK
    | typeof LAST_MONTH
    | typeof LAST_THREE_MONTHS
    | typeof LAST_N_DAYS
    | typeof TOMORROW
    | typeof NEXT_WEEK
    | typeof NEXT_MONTH
    | typeof NEXT_THREE_MONTHS
    | typeof NEXT_N_DAYS
    | typeof PERIOD;

  export type ModelVisitFilterGroupFilter = {
    IsInclude: boolean;
    Values: ModelVisitFilter[] | null;
  };

  export type ModelSaleFilterGroupFilter = {
    IsInclude: boolean;
    Values: ModelSaleFilter[] | null;
  };

  export type ModelSaleFilter = {
    ProductIds: number[] | null;
  };

  export type ModelClientFilterGroupFilter = {
    IsInclude: boolean;
    Values: ModelClientFilter[] | null;
  };

  export type ModelClientFilter = {
    Gender: string | null;
    TagIds: number[] | null;
    MinServicesCost: number | null;
    MaxServicesCost: number | null;
    IsReceivedSMS: boolean | null;
    DateStart: string | null;
    DateEnd: string | null;
    Birthday: Period;
    OffsetDays: number | null;
    MinAge: number | null;
    MaxAge: number | null;
    BalanceS: number | null;
    BalanceE: number | null;
  };
}
